import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Switch } from '@/components/ui/Switch';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '@/components/ui/Dialog';
import { Checkbox } from '@/components/ui/Checkbox';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { getCarryForwardRules, saveCarryForwardRule, updateCarryForwardRule, deleteCarryForwardRule, getPolicyLeaveTypes } from '../../../../../api/Attendance/leaves';
import type { LeavePolicyView, CarryForwardRuleView, CarryForwardRuleInput, LeaveTypeView } from '../../../../../models/attendance';

interface CarryForwardTabProps {
  currentPolicy?: LeavePolicyView;
}

interface GlobalSettings {
  fiscalYearEnd: string;
  calculationTiming: string;
  defaultNotificationPeriod: number;
  defaultProcessingMode: string;
}

const emptyRuleInput: CarryForwardRuleInput = {
  leaveTypeId: 0,
  isEnabled: true,
  calculationType: 'percentage',
  calculationBase: 'remaining_balance',
  calculationValue: 0,
  maxCapDays: 0,
  expirationMonths: 0,
  expirationHandling: 'forfeit',
  cashConversionIncludeBasic: false,
  cashConversionIncludeAllowances: false,
  cashConversionPercentage: 100,
  usageOrder: 'carry_forward_first',
  autoForfeit: false,
  notificationDaysBefore: 0,
  minTenureMonths: undefined,
  minUtilizationPercent: undefined,
  eligibilityLogic: 'all',
  prorationMethod: 'none',
  fiscalYearStart: undefined,
  fiscalYearEnd: undefined,
};

const CarryForwardTab: React.FC<CarryForwardTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');
  // CO-575 w2 — the refusal surface for the enable/disable switch (see toggleRule).
  const { toast, showToast, dismissToast } = useToast();

  const [rules, setRules] = useState<CarryForwardRuleView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>({
    fiscalYearEnd: '', calculationTiming: 'fiscal_year_end',
    defaultNotificationPeriod: 30, defaultProcessingMode: 'automatic'
  });
  const [showGlobalModal, setShowGlobalModal] = useState(false);
  const [editingGlobal, setEditingGlobal] = useState<GlobalSettings>({ ...globalSettings });

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<CarryForwardRuleView | null>(null);
  const [formData, setFormData] = useState<CarryForwardRuleInput>({ ...emptyRuleInput });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (currentPolicy?.id) {
      loadRules();
      loadLeaveTypes();
    }
  }, [currentPolicy?.id]);

  useEffect(() => {
    const firstEnabled = rules.find(r => r.isEnabled);
    if (firstEnabled) {
      setGlobalSettings(prev => ({
        ...prev,
        fiscalYearEnd: firstEnabled.fiscalYearEnd ?? prev.fiscalYearEnd,
      }));
    }
  }, [rules]);

  const loadRules = async () => {
    if (!currentPolicy?.id) return;
    setIsLoading(true);
    try {
      const result = await getCarryForwardRules(currentPolicy.id);
      if (!result?.errors?.length) {
        setRules(result.payload ?? []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    if (!currentPolicy?.id) return;
    try {
      const result = await getPolicyLeaveTypes(currentPolicy.id);
      if (!result?.errors?.length) {
        setLeaveTypes(result.payload ?? []);
      }
    } catch { /* ignore */ }
  };

  const handleSaveGlobal = (): void => {
    setGlobalSettings(editingGlobal);
    setShowGlobalModal(false);
  };

  const handleEditRule = (rule: CarryForwardRuleView): void => {
    setEditingRule(rule);
    setFormData({
      leaveTypeId: rule.leaveTypeId,
      isEnabled: rule.isEnabled,
      calculationType: rule.calculationType,
      calculationBase: rule.calculationBase,
      calculationValue: rule.calculationValue,
      maxCapDays: rule.maxCapDays,
      expirationMonths: rule.expirationMonths,
      expirationHandling: rule.expirationHandling,
      cashConversionIncludeBasic: rule.cashConversionIncludeBasic,
      cashConversionIncludeAllowances: rule.cashConversionIncludeAllowances,
      cashConversionPercentage: rule.cashConversionPercentage,
      usageOrder: rule.usageOrder,
      autoForfeit: rule.autoForfeit,
      notificationDaysBefore: rule.notificationDaysBefore,
      minTenureMonths: rule.minTenureMonths,
      minUtilizationPercent: rule.minUtilizationPercent,
      eligibilityLogic: rule.eligibilityLogic,
      prorationMethod: rule.prorationMethod,
      fiscalYearStart: rule.fiscalYearStart,
      fiscalYearEnd: rule.fiscalYearEnd,
    });
    setShowRuleModal(true);
  };

  const handleAddRule = (): void => {
    setEditingRule(null);
    setFormData({ ...emptyRuleInput });
    setShowRuleModal(true);
  };

  const handleSaveRule = async (): Promise<void> => {
    if (!currentPolicy?.id || !formData.leaveTypeId) return;
    setIsSaving(true);
    try {
      const result = editingRule
        ? await updateCarryForwardRule(editingRule.id, formData)
        : await saveCarryForwardRule(currentPolicy.id, formData);
      if (!result?.errors?.length) {
        await loadRules();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number): Promise<void> => {
    if (!currentPolicy?.id) return;
    if (!window.confirm(t('Are you sure you want to delete this item?'))) return;
    setIsSaving(true);
    try {
      const result = await deleteCarryForwardRule(currentPolicy.id, ruleId);
      if (!result?.errors?.length) {
        await loadRules();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = (): void => {
    setShowRuleModal(false);
    setEditingRule(null);
    setFormData({ ...emptyRuleInput });
    setFormErrors({});
  };

  const toggleRule = async (rule: CarryForwardRuleView): Promise<void> => {
    if (!currentPolicy?.id) return;
    const input: CarryForwardRuleInput = {
      leaveTypeId: rule.leaveTypeId,
      isEnabled: !rule.isEnabled,
      calculationType: rule.calculationType,
      calculationBase: rule.calculationBase,
      calculationValue: rule.calculationValue,
      maxCapDays: rule.maxCapDays,
      expirationMonths: rule.expirationMonths,
      expirationHandling: rule.expirationHandling,
      cashConversionIncludeBasic: rule.cashConversionIncludeBasic,
      cashConversionIncludeAllowances: rule.cashConversionIncludeAllowances,
      cashConversionPercentage: rule.cashConversionPercentage,
      usageOrder: rule.usageOrder,
      autoForfeit: rule.autoForfeit,
      notificationDaysBefore: rule.notificationDaysBefore,
      minTenureMonths: rule.minTenureMonths,
      minUtilizationPercent: rule.minUtilizationPercent,
      eligibilityLogic: rule.eligibilityLogic,
      prorationMethod: rule.prorationMethod,
      fiscalYearStart: rule.fiscalYearStart,
      fiscalYearEnd: rule.fiscalYearEnd,
    };
    try {
      const result = await updateCarryForwardRule(rule.id, input);
      if (!result?.errors?.length) {
        await loadRules();
      } else {
        showToast(getApiErrorMessage(result, t, t('carryForwardToggleError')), 'error');
      }
    } catch (err) {
      // CO-575 w2: `/* ignore */` on a WRITE. The switch is rendered from `rules`, which the failed
      // update never changed, so the toggle silently snapped back to where it was — indistinguishable
      // from a mis-click. Carry-forward decides whether unused leave survives the year end; a refusal
      // here has to be stated.
      showToast(getApiErrorMessage(err, t, t('carryForwardToggleError')), 'error');
    }
  };

  const formatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const availableLeaveTypes = leaveTypes.filter(
    lt => !rules.some(r => r.leaveTypeId === lt.id) || editingRule?.leaveTypeId === lt.id
  );

  const validateForm = (data: CarryForwardRuleInput): Record<string, string> => {
    const errors: Record<string, string> = {};
    if ((data.calculationValue ?? 0) < 0)
      errors.calculationValue = t('carryForwardNegativeCalculationValue');
    if ((data.maxCapDays ?? 0) < 0)
      errors.maxCapDays = t('carryForwardNegativeMaxCap');
    if (data.expirationHandling && data.expirationHandling !== 'none' && (data.expirationMonths ?? 0) <= 0)
      errors.expirationMonths = t('carryForwardExpirationMonthsRequired');
    if (data.fiscalYearStart && data.fiscalYearEnd && data.fiscalYearStart > data.fiscalYearEnd)
      errors.fiscalYear = t('carryForwardFiscalYearStartAfterEnd');
    return errors;
  };

  useEffect(() => {
    if (showRuleModal) {
      setFormErrors(validateForm(formData));
    }
  }, [formData, showRuleModal]);

  const hasFormErrors = Object.keys(formErrors).length > 0;

  if (!currentPolicy) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon name="AlertCircle" size={48} className="text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground text-lg">{t('Please select a policy to manage carry-forward rules.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-warning/30 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-warning mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Carry-Forward Rules Configuration')}</h3>
            <p className="text-sm text-foreground">
              {t('Define rules for carrying forward unused leave balances to the next year. Configure maximum limits, expiry dates, and conversion options.')}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-muted border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Shield" size={16} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h4 className="text-md font-semibold text-foreground mb-4">{t('Carry-Forward Statistics')}</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-primary/10 rounded-lg">
            <div className="text-2xl font-bold text-primary">&mdash;</div>
            <div className="text-sm text-muted-foreground">{t('Total Days Carried')}</div>
          </div>
          <div className="text-center p-4 bg-success/10 rounded-lg">
            <div className="text-2xl font-bold text-success">&mdash;</div>
            <div className="text-sm text-muted-foreground">{t('Days Used')}</div>
          </div>
          <div className="text-center p-4 bg-destructive/10 rounded-lg">
            <div className="text-2xl font-bold text-destructive">&mdash;</div>
            <div className="text-sm text-muted-foreground">{t('Days Forfeited')}</div>
          </div>
          <div className="text-center p-4 bg-warning/10 rounded-lg">
            <div className="text-2xl font-bold text-warning">&mdash;</div>
            <div className="text-sm text-muted-foreground">{t('Utilization Rate')}</div>
          </div>
        </div>
      </div>

      {/* Global Settings */}
      <div className="bg-muted rounded-lg p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Icon name="Settings" size={20} className="text-primary" />
            <h4 className="text-md font-semibold text-foreground">{t('Global Carry-Forward Settings')}</h4>
          </div>
          <Button variant="outline" size="sm" iconName="Edit" onClick={() => { setEditingGlobal({ ...globalSettings }); setShowGlobalModal(true); }} data-testid="leave-cf-edit-global">
            {t('Edit Global Settings')}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
            <Icon name="Calendar" size={18} className="text-primary mb-2" />
            <div className="text-xl font-bold text-primary">{globalSettings.fiscalYearEnd ? formatDate(globalSettings.fiscalYearEnd) : '\u2014'}</div>
            <div className="text-sm text-muted-foreground">{t('Fiscal Year End')}</div>
          </div>
          <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
            <Icon name="Clock" size={18} className="text-success mb-2" />
            <div className="text-xl font-bold text-success">
              {globalSettings?.calculationTiming === 'fiscal_year_end' ? t('Fiscal Year') : t('Anniversary')}
            </div>
            <div className="text-sm text-muted-foreground">{t('Calculation Timing')}</div>
          </div>
          <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
            <Icon name="Bell" size={18} className="text-warning mb-2" />
            <div className="text-xl font-bold text-warning">{globalSettings?.defaultNotificationPeriod} {t('Days')}</div>
            <div className="text-sm text-muted-foreground">{t('Default Notification')}</div>
          </div>
          <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
            <Icon name="Zap" size={18} className="text-warning mb-2" />
            <div className="text-xl font-bold text-warning capitalize">{globalSettings?.defaultProcessingMode === 'automatic' ? t('Automatic') : t('Manual')}</div>
            <div className="text-sm text-muted-foreground">{t('Processing Mode')}</div>
          </div>
        </div>
      </div>

      {/* Rules per Leave Type */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Icon name="FileText" size={20} className="text-foreground" />
            <h4 className="text-md font-semibold text-foreground">{t('Carry-Forward Rules by Leave Type')}</h4>
          </div>
          <Button variant="default" size="sm" iconName="Plus" onClick={handleAddRule} data-testid="add-carry-forward-btn" disabled={availableLeaveTypes.length === 0}>
            {t('Add Rule')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <span className="ms-3 text-muted-foreground">{t('Loading carry-forward rules...')}</span>
          </div>
        ) : rules.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-card border border-border rounded-lg">
            <Icon name="FileText" size={48} className="text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground text-lg mb-2">{t('No carry-forward rules configured')}</p>
            <p className="text-muted-foreground text-sm mb-4">{t('Add rules to define how unused leave balances carry forward.')}</p>
            <Button variant="default" size="sm" iconName="Plus" onClick={handleAddRule} disabled={availableLeaveTypes.length === 0}>
              {t('Add First Rule')}
            </Button>
          </div>
        ) : (
          rules.map((rule) => (
            <div key={rule.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow" data-testid="carry-forward-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <h4 className="text-md font-semibold text-foreground">{rule.leaveTypeName}</h4>
                      {rule.isEnabled && <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full font-medium">{t('Active')}</span>}
                    </div>
                  </div>
                  <Switch
                    checked={rule.isEnabled}
                    onCheckedChange={() => toggleRule(rule)}
                    data-testid="leave-cf-toggle-rule"
                  />
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditRule(rule)} data-testid="leave-cf-edit-rule" />
                  <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => handleDeleteRule(rule.id)} data-testid="leave-cf-delete-rule" />
                </div>
              </div>

              {rule.isEnabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Carry-Forward Amount')}</div>
                      <div className="text-lg font-bold text-primary">
                        {rule.calculationType === 'percentage'
                          ? `${rule.calculationValue}% ${t('of')} ${t((rule.calculationBase ?? '').replace(/_/g, ' '))}`
                          : `${rule.calculationValue} ${t('days')}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Maximum Cap')}</div>
                      <div className="text-lg font-bold text-warning">{rule.maxCapDays} {t('days')}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Expiration')}</div>
                      <div className="text-lg font-bold text-warning">{rule.expirationMonths} {t('months')}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Usage Order')}</div>
                      <div className="text-lg font-bold text-success">
                        {rule.usageOrder === 'carry_forward_first' ? t('CF First') : t('Current First')}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                        <Icon name="AlertTriangle" size={16} className="text-warning" />
                        <h5 className="text-sm font-semibold text-foreground">{t('Expiration Handling')}</h5>
                      </div>
                      <div className="text-sm text-foreground">
                        <span className="font-medium capitalize">{t((rule.expirationHandling ?? '').replace(/_/g, ' '))}</span> {t('after')} {rule.expirationMonths} {t('months')}
                      </div>
                    </div>
                    <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                        <Icon name="Bell" size={16} className="text-destructive" />
                        <h5 className="text-sm font-semibold text-foreground">{t('Forfeiture Settings')}</h5>
                      </div>
                      {rule.autoForfeit ? (
                        <div className="text-sm text-foreground">
                          <div>{t('Notify')} {rule.notificationDaysBefore} {t('days before')}</div>
                          <div className="mt-1">
                            <span className="text-destructive font-medium">{t('Auto-forfeit enabled')}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-foreground">
                          <div>{t('Notify')} {rule.notificationDaysBefore} {t('days before')}</div>
                          <div className="mt-1">
                            <span className="text-muted-foreground">{t('Manual forfeiture')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {(rule.minTenureMonths != null || rule.minUtilizationPercent != null) && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse mb-3">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        <h5 className="text-sm font-semibold text-foreground">{t('Eligibility Conditions')}</h5>
                        {rule.eligibilityLogic && (
                          <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                            {rule.eligibilityLogic === 'all' ? t('All must be met (AND)') : t('Any must be met (OR)')}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {rule.minTenureMonths != null && (
                          <span className="px-3 py-1.5 bg-success/10 text-success text-sm rounded-lg border border-success/20 font-medium">
                            {t('Minimum')} {rule.minTenureMonths} {t('months')} {t('tenure')}
                          </span>
                        )}
                        {rule.minUtilizationPercent != null && (
                          <span className="px-3 py-1.5 bg-success/10 text-success text-sm rounded-lg border border-success/20 font-medium">
                            {t('Minimum')} {rule.minUtilizationPercent}% {t('utilization')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Global Settings Modal */}
      <Dialog open={showGlobalModal} onOpenChange={(open) => { if (!open) { setEditingGlobal({ ...globalSettings }); setShowGlobalModal(false); } }}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{t('Edit Global Carry-Forward Settings')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Fiscal Year End Date')}</label>
                <Input type="date" value={editingGlobal?.fiscalYearEnd} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingGlobal({ ...editingGlobal, fiscalYearEnd: e?.target?.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Calculation Timing')}</label>
                <Select
                  value={editingGlobal?.calculationTiming}
                  onChange={(value: string) => setEditingGlobal({ ...editingGlobal, calculationTiming: value })}
                  options={[
                    { value: 'fiscal_year_end', label: t('End of Fiscal Year') },
                    { value: 'employee_anniversary', label: t('Employee Anniversary') }
                  ]}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Default Notification Period (days)')}</label>
                <Input type="number" value={editingGlobal?.defaultNotificationPeriod} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingGlobal({ ...editingGlobal, defaultNotificationPeriod: parseInt(e?.target?.value) || 0 })} min="0" max="365" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Default Processing Mode')}</label>
                <Select
                  value={editingGlobal?.defaultProcessingMode}
                  onChange={(value: string) => setEditingGlobal({ ...editingGlobal, defaultProcessingMode: value })}
                  options={[{ value: 'automatic', label: t('Automatic') }, { value: 'manual', label: t('Manual') }]}
                />
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingGlobal({ ...globalSettings }); setShowGlobalModal(false); }} data-testid="leave-cf-global-modal-cancel">{t('Cancel')}</Button>
            <Button variant="default" onClick={handleSaveGlobal} data-testid="leave-cf-global-modal-save">{t('Save Global Settings')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule Edit Modal */}
      <Dialog open={showRuleModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? t('Edit Carry-Forward Rule') : t('Add Carry-Forward Rule')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            {/* Basic */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Leave Type')} *</label>
                <Select
                  value={String(formData.leaveTypeId || '')}
                  onChange={(value: string) => setFormData({ ...formData, leaveTypeId: parseInt(value) || 0 })}
                  options={[
                    { value: '', label: t('Select Leave Type') },
                    ...availableLeaveTypes.map(lt => ({ value: String(lt.id), label: lt.name }))
                  ]}
                  disabled={!!editingRule}
                />
              </div>
              <div className="flex items-end">
                <Checkbox
                  checked={formData.isEnabled}
                  onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                  label={t('Enable this rule')}
                />
              </div>
            </div>

            {formData.isEnabled && (
              <>
                {/* Calculation */}
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Carry-Forward Calculation')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Type')}</label>
                      <Select value={formData.calculationType ?? ''} onChange={(value: string) => setFormData({ ...formData, calculationType: value })}
                        options={[{ value: 'fixed_days', label: t('Fixed Days') }, { value: 'percentage', label: t('Percentage') }]} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Base')}</label>
                      <Select value={formData.calculationBase ?? ''} onChange={(value: string) => setFormData({ ...formData, calculationBase: value })}
                        options={[{ value: 'remaining_balance', label: t('Remaining Balance') }, { value: 'total_entitlement', label: t('Total Entitlement') }, { value: 'accrued_amount', label: t('Accrued Amount') }]} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{formData.calculationType === 'percentage' ? t('Percentage (%)') : t('Days')}</label>
                      <Input type="number" value={formData.calculationValue} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, calculationValue: parseInt(e.target.value) || 0 })} min="0" />
                      {formErrors.calculationValue && <p className="text-xs text-destructive mt-1" role="alert">{formErrors.calculationValue}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Max Cap (days)')}</label>
                      <Input type="number" value={formData.maxCapDays} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, maxCapDays: parseInt(e.target.value) || 0 })} min="0" />
                      {formErrors.maxCapDays && <p className="text-xs text-destructive mt-1" role="alert">{formErrors.maxCapDays}</p>}
                    </div>
                  </div>
                </div>

                {/* Expiration */}
                <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Expiration Behavior')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Expiration Period')}</label>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Input type="number" value={formData.expirationMonths} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, expirationMonths: parseInt(e.target.value) || 0 })} min="0" max="24" />
                        <span className="text-sm text-muted-foreground">{t('months')}</span>
                      </div>
                      {formErrors.expirationMonths && <p className="text-xs text-destructive mt-1" role="alert">{formErrors.expirationMonths}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Expired Balance Handling')}</label>
                      <Select value={formData.expirationHandling ?? ''} onChange={(value: string) => setFormData({ ...formData, expirationHandling: value })}
                        options={[{ value: 'forfeit', label: t('Forfeit') }, { value: 'convert_unpaid', label: t('Convert to Unpaid') }, { value: 'convert_cash', label: t('Convert to Cash') }]} />
                    </div>
                  </div>
                </div>

                {/* Usage & Forfeiture */}
                <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Usage & Forfeiture')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Usage Order')}</label>
                      <Select value={formData.usageOrder ?? ''} onChange={(value: string) => setFormData({ ...formData, usageOrder: value })}
                        options={[{ value: 'carry_forward_first', label: t('Use Carry-Forward First') }, { value: 'current_year_first', label: t('Use Current Year First') }]} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Notification Days Before')}</label>
                      <Input type="number" value={formData.notificationDaysBefore} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, notificationDaysBefore: parseInt(e.target.value) || 0 })} min="0" />
                    </div>
                    <div className="flex items-end">
                      <Checkbox
                        checked={formData.autoForfeit ?? false}
                        onChange={(e) => setFormData({ ...formData, autoForfeit: e.target.checked })}
                        label={t('Auto-forfeit')}
                      />
                    </div>
                  </div>
                </div>

                {/* Cash Conversion */}
                <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Cash Conversion')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex items-end">
                      <Checkbox
                        checked={formData.cashConversionIncludeBasic ?? false}
                        onChange={(e) => setFormData({ ...formData, cashConversionIncludeBasic: e.target.checked })}
                        label={t('Include Basic Salary')}
                      />
                    </div>
                    <div className="flex items-end">
                      <Checkbox
                        checked={formData.cashConversionIncludeAllowances ?? false}
                        onChange={(e) => setFormData({ ...formData, cashConversionIncludeAllowances: e.target.checked })}
                        label={t('Include Allowances')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Conversion Percentage (%)')}</label>
                      <Input type="number" value={formData.cashConversionPercentage} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cashConversionPercentage: parseInt(e.target.value) || 0 })} min="0" max="100" />
                    </div>
                  </div>
                </div>

                {/* Eligibility */}
                <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Eligibility Conditions')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Eligibility Logic')}</label>
                      <Select value={formData.eligibilityLogic ?? 'all'} onChange={(value: string) => setFormData({ ...formData, eligibilityLogic: value })}
                        options={[{ value: 'all', label: t('All conditions (AND)') }, { value: 'any', label: t('Any condition (OR)') }]} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Min Tenure (months)')}</label>
                      <Input type="number" value={formData.minTenureMonths ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, minTenureMonths: e.target.value ? parseInt(e.target.value) : undefined })} min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Min Utilization (%)')}</label>
                      <Input type="number" value={formData.minUtilizationPercent ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, minUtilizationPercent: e.target.value ? parseInt(e.target.value) : undefined })} min="0" max="100" />
                    </div>
                  </div>
                </div>

                {/* Proration & Fiscal Year */}
                <div className="p-4 bg-muted rounded-lg border border-border">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Proration & Fiscal Year')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Proration Method')}</label>
                      <Select value={formData.prorationMethod ?? 'none'} onChange={(value: string) => setFormData({ ...formData, prorationMethod: value })}
                        options={[{ value: 'none', label: t('None') }, { value: 'monthly_calendar', label: t('Monthly Calendar') }, { value: 'daily', label: t('Daily') }]} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Fiscal Year Start')}</label>
                      <Input type="date" value={formData.fiscalYearStart ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, fiscalYearStart: e.target.value || undefined })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Fiscal Year End')}</label>
                      <Input type="date" value={formData.fiscalYearEnd ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, fiscalYearEnd: e.target.value || undefined })} />
                    </div>
                  </div>
                  {formErrors.fiscalYear && <p className="text-xs text-destructive mt-1" role="alert">{formErrors.fiscalYear}</p>}
                </div>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} data-testid="leave-cf-rule-modal-cancel">{t('Cancel')}</Button>
            <Button variant="default" onClick={handleSaveRule} disabled={!formData.leaveTypeId || isSaving || hasFormErrors} data-testid="leave-cf-rule-modal-save">
              {isSaving ? t('Saving...') : (editingRule ? t('Update Rule') : t('Add Rule'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CO-575 w2 — the refusal surface for the enable/disable switch. */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default CarryForwardTab;
